import React, { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, List, ListItem, ListItemText, MenuItem, Stack, TextField, Typography } from '@mui/material';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { aiLearningPathService, type AiPathDraft, type AiPathStep } from '../../services/aiLearningPathService';

/**
 * #519 — "Lernpfad mit KI erstellen": topic, goal and level → draft (ordered
 * courses with a reason each) → the learner edits title and steps → path is
 * created. In manager mode the same dialog creates an inactive company draft
 * with an optional path certificate.
 */
export interface CreateAiLearningPathDialogProps {
    open: boolean;
    onClose: () => void;
    onCreated: (path: { id: number; personal: boolean }) => void;
    /** Content managers: offer "company path" + certificate mode. */
    managerMode?: boolean;
}

const LEVELS = ['beginner', 'intermediate', 'advanced'] as const;

export const CreateAiLearningPathDialog: React.FC<CreateAiLearningPathDialogProps> = ({ open, onClose, onCreated, managerMode = false }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [topic, setTopic] = useState('');
    const [goal, setGoal] = useState('');
    const [level, setLevel] = useState<string>('beginner');
    const [draft, setDraft] = useState<AiPathDraft | null>(null);
    const [title, setTitle] = useState('');
    const [steps, setSteps] = useState<AiPathStep[]>([]);
    const [personal, setPersonal] = useState(!managerMode);
    const [pathCertificate, setPathCertificate] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const apiError = (e: unknown, fallback: string): string => {
        const data = (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
        return data?.message ?? data?.error ?? fallback;
    };

    const reset = () => { setDraft(null); setSteps([]); setTitle(''); setError(null); };

    const preview = async () => {
        setBusy(true);
        setError(null);
        try {
            const d = await aiLearningPathService.preview(topic.trim(), goal.trim(), level);
            setDraft(d);
            setTitle(d.title);
            setSteps(d.steps);
        } catch (e) {
            setError(apiError(e, translate('learningPaths.ai.previewError')));
        } finally {
            setBusy(false);
        }
    };

    const create = async () => {
        setBusy(true);
        try {
            const created = await aiLearningPathService.create({
                title: title.trim(),
                goal: goal.trim() || null,
                level,
                steps: steps.map((s) => ({ course_id: s.course_id, reason: s.reason })),
                personal: managerMode ? personal : true,
                certificate_mode: pathCertificate ? 'path' : 'per_course',
            });
            notify(translate('learningPaths.ai.created', { title: created.title }), { type: 'success' });
            reset();
            onCreated(created);
        } catch (e) {
            notify(apiError(e, translate('learningPaths.ai.createError')), { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const hours = steps.reduce((sum, s) => sum + (s.estimated_duration ?? 0), 0) / 60;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth data-testid="ai-path-dialog">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AutoAwesomeOutlined fontSize="small" />{translate('learningPaths.ai.title')}</DialogTitle>
            <DialogContent dividers>
                {!draft ? (
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">{translate('learningPaths.ai.intro')}</Typography>
                        <TextField label={translate('learningPaths.ai.topic')} value={topic} onChange={(e) => setTopic(e.target.value)} required fullWidth inputProps={{ 'aria-label': translate('learningPaths.ai.topic') }} />
                        <TextField label={translate('learningPaths.ai.goal')} value={goal} onChange={(e) => setGoal(e.target.value)} multiline minRows={2} fullWidth inputProps={{ 'aria-label': translate('learningPaths.ai.goal') }} />
                        <TextField select label={translate('learningPaths.ai.level')} value={level} onChange={(e) => setLevel(e.target.value)} fullWidth>
                            {LEVELS.map((l) => <MenuItem key={l} value={l}>{translate(`learningPaths.${l}`)}</MenuItem>)}
                        </TextField>
                        {error && <Typography color="error" data-testid="ai-path-error">{error}</Typography>}
                    </Stack>
                ) : (
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">{translate('learningPaths.ai.reviewHint')}</Typography>
                        <TextField label={translate('learningPaths.ai.pathTitle')} value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth inputProps={{ 'aria-label': translate('learningPaths.ai.pathTitle') }} />
                        <List dense data-testid="ai-path-steps">
                            {steps.map((step, index) => (
                                <ListItem key={step.course_id} secondaryAction={<IconButton edge="end" aria-label={translate('learningPaths.ai.removeStep', { title: step.course_title })} onClick={() => setSteps((prev) => prev.filter((s) => s.course_id !== step.course_id))}><DeleteOutline fontSize="small" /></IconButton>}>
                                    <ListItemText primary={`${index + 1}. ${step.course_title}`} secondary={step.reason} />
                                </ListItem>
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{translate('learningPaths.ai.estimate', { steps: steps.length, hours: hours.toFixed(1) })}</Typography>
                        {managerMode && (
                            <>
                                <FormControlLabel control={<Checkbox checked={!personal} onChange={(e) => setPersonal(!e.target.checked)} />} label={translate('learningPaths.ai.companyPath')} />
                                <FormControlLabel control={<Checkbox checked={pathCertificate} onChange={(e) => setPathCertificate(e.target.checked)} />} label={translate('learningPaths.ai.pathCertificate')} />
                            </>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {draft && <Button onClick={reset} disabled={busy}>{translate('learningPaths.ai.back')}</Button>}
                <Button onClick={onClose} disabled={busy}>{translate('ra.action.cancel')}</Button>
                {!draft ? (
                    <Button variant="contained" onClick={() => void preview()} disabled={busy || topic.trim().length < 3} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeOutlined />}>
                        {translate('learningPaths.ai.preview')}
                    </Button>
                ) : (
                    <Button variant="contained" onClick={() => void create()} disabled={busy || steps.length === 0 || title.trim().length < 3} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
                        {translate('learningPaths.ai.create')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CreateAiLearningPathDialog;
